import axios from 'axios';
import vopayApi from '@/services/api.config';
import { SupportedCurrencies } from 'vopay';
import { useEffect, useState } from 'react';

const { api } = vopayApi;

export type Balance = {
  AccountBalance: number;
  AvailableFunds: number;
  Currency: SupportedCurrencies;
  PendingFunds: number;
  Reserve: number;
  RollingReserveNSF: number;
  RollingReserveReturns: number;
  RollupAvailableBalance: number | null;
};

export default function useAccountBalances() {
  const source = axios.CancelToken.source();
  const [balances, setBalances] = useState<Balance[]>([]);
  const [fetchingBalances, setFetchingBalances] = useState(false);

  useEffect(() => {
    fetchBalances();

    return () => source.cancel();
  }, []);

  async function fetchBalances() {
    try {
      setFetchingBalances(true);
      const response = await api.fetchAccountBalances({}, source);

      if (!response.Success) {
        throw new Error(response.ErrorMessage);
      }

      const parsedBalances = response.AccountBalances.map((balance) => ({
        AccountBalance: balance.AccountBalance,
        AvailableFunds: balance.AvailableFunds,
        Currency: balance.Currency,
        PendingFunds: balance.PendingFunds,
        Reserve: balance.Reserve,
        RollingReserveNSF: balance.RollingReserveNSF ?? 0,
        RollingReserveReturns: balance.RollingReserveReturns ?? 0,
        RollupAvailableBalance: balance.RollupAvailableBalance ?? null,
      }));

      setBalances(parsedBalances);
    } catch (err) {
      console.error(err);

      if (err instanceof Error) throw err;

      throw new Error('An error occurred while fetching account balances');
    } finally {
      setFetchingBalances(false);
    }
  }

  return {
    fetchingBalances,
    balances,
    fetchBalances,
  };
}
